import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Paper,
  Alert,
  Button,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { auditActionLabel, auditResourceLabel } from '../utils/displayLabels';
import { DateRangeFields, PageContainer, PageHeader } from '../components/ui';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

interface AuditLogEntry {
  id: string;
  action: string;
  resourceType: string;
  resourceId?: string | null;
  method: string;
  path: string;
  actorId: string;
  actorEmail?: string | null;
  actorName?: string | null;
  statusCode: number;
  createdAt?: any;
  metadata?: {
    resourceName?: string | null;
    resourceEmail?: string | null;
    isPriceChange?: boolean;
    isProductApproval?: boolean;
  };
}

const formatTimestamp = (value: any) => {
  if (!value) return '—';
  const date =
    value?.toDate?.() ||
    (value?.seconds ? new Date(value.seconds * 1000) : null) ||
    (value?._seconds ? new Date(value._seconds * 1000) : null) ||
    new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const getMethodColor = (method: string) => {
  switch (method) {
    case 'POST':
      return 'success';
    case 'PUT':
    case 'PATCH':
      return 'warning';
    case 'DELETE':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Human action text for a row, plus the raw call kept as secondary text.
 * `showRaw` is false when no rule matched and the label already is the raw call.
 */
const describeAction = (log: AuditLogEntry) => {
  const raw = log.action || `${log.method || ''} ${log.path || ''}`.trim();
  const label = auditActionLabel(log.method, log.path, raw);
  return { label, raw, showRaw: label !== raw };
};

export const AdminAuditLogPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Search runs on the server across the filtered window, so wait for a pause in typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // The backend response has no total count (see adminAuditLogService.list),
  // so we request one extra row per page to know whether another page exists.
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin-audit-logs', page, rowsPerPage, resourceFilter, debouncedSearch, fromDate, toDate],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const limit = rowsPerPage + 1;
      const offset = page * rowsPerPage;
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (resourceFilter) params.set('resourceType', resourceFilter);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      const response = await makeApiCall(`/admin/audit-logs?${params.toString()}`, { headers });
      if (!response.ok) {
        throw new Error('Failed to load audit logs');
      }
      return response.json();
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const { data: resourceTypesData } = useQuery({
    queryKey: ['admin-audit-log-resource-types'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/admin/audit-logs/resource-types', { headers });
      if (!response.ok) {
        throw new Error('Failed to load audit log resource types');
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const fetchedLogs: AuditLogEntry[] = data?.data?.logs || [];
  const hasNextPage = fetchedLogs.length > rowsPerPage;
  const logs = hasNextPage ? fetchedLogs.slice(0, rowsPerPage) : fetchedLogs;
  // Unknown total: -1 tells TablePagination to render "more than N" and we
  // disable the next arrow ourselves once a short page tells us we're at the end.
  const totalCount = hasNextPage ? -1 : page * rowsPerPage + logs.length;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResourceFilter = (value: string) => {
    setResourceFilter(value);
    setPage(0);
  };

  const handleDateChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(0);
  };

  const knownResourceTypes: string[] = resourceTypesData?.data?.resourceTypes || [];

  const resourceTypes = useMemo(
    () =>
      Array.from(
        new Set([...knownResourceTypes, ...logs.map(log => log.resourceType), resourceFilter].filter(Boolean))
      ).sort(),
    [knownResourceTypes, logs, resourceFilter]
  );

  return (
    <PageContainer sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Admin Audit Log"
        description="Track admin mutations across the platform, including price changes and product approvals."
        actions={
          <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Action, actor, resource"
              helperText="Searches the most recent matching entries"
              fullWidth
            />
            <TextField
              select
              label="Resource type"
              value={resourceFilter}
              onChange={(event) => handleResourceFilter(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All resource types</MenuItem>
              {resourceTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {auditResourceLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            <DateRangeFields
              start={fromDate}
              end={toDate}
              onStartChange={handleDateChange(setFromDate)}
              onEndChange={handleDateChange(setToDate)}
              startLabel="From"
              endLabel="To"
              size="medium"
            />
          </Stack>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error">Failed to load audit logs.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Resource</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map(log => {
                      const action = describeAction(log);
                      return (
                        <TableRow key={log.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(log.createdAt)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={log.method} color={getMethodColor(log.method) as any} sx={{ mr: 1 }} />
                            {action.label}
                            {action.showRaw && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', wordBreak: 'break-all' }}
                              >
                                {action.raw}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                              <Typography variant="body2">{auditResourceLabel(log.resourceType)}</Typography>
                              {log.metadata?.isPriceChange && (
                                <Chip size="small" label="price" variant="outlined" color="warning" />
                              )}
                              {log.metadata?.isProductApproval && (
                                <Chip size="small" label="approval" variant="outlined" color="success" />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {log.metadata?.resourceName || log.metadata?.resourceEmail || log.resourceId || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{log.actorName || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.actorEmail || log.actorId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={log.statusCode} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {logs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            No audit log entries found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {data?.data?.scanCapped && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Search covered the most recent {data.data.scanned} entries in this range. Narrow the dates to look further back.
                </Typography>
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                nextIconButtonProps={{ disabled: !hasNextPage }}
              />
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
};
